<script lang="ts" setup>
import { getCourse } from '__generated__/getCourse'
import { getHolesForCourse } from '__generated__/getHolesForCourse'

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

import { useClient } from '@base/api/useClient'

import getSlug from '@base/lib/getSlug'
import { createSlug } from '@base/lib/createSlug'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'

import { useRouterStore } from '@base/store/routerStore'
import { useToastsStore } from '@base/store/toastsStore'

import { ToastSemantic } from '@base/_types/Toast'

import HeaderContent from '@base/components/HeaderContent/HeaderContent.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import LinkWrapper from '@base/components/LinkWrapper/LinkWrapper.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USMediaSlide from '@base/components/USMediaSlide/USMediaSlide.vue'
import USMediaSlider from '@base/components/USMediaSlider/USMediaSlider.vue'
import USShareButton from '@base/components/USShareButton/USShareButton.vue'

import { playerTitle } from '@player/_lib/playerTitle'

import { coursesRouteNames } from '@player/courses/router/routeNames'
import { GET_COURSE_QUERY } from '@player/courses/api/queries/getCourse'
import { GET_HOLES_FOR_COURSE_QUERY } from '@player/courses/api/queries/getCourseHoles'

import { props as propDefinition } from './props'

// MockData
const mockData: any = {
  hole: {
    description: `
      The first of two par 5s is a medium-length, dog-leg left par 5 that looks fairly
      straightforward from the tee. However, with a perfectly placed tee shot from medium
      to long hitters, this hole becomes the ultimate in risk/reward. Going for the green
      in two will require an extremely precise shot, with a large pond protecting the
      majority of green.
    `,
    par: 72,
    // map: 'https://images.unsplash.com/photo-1627742259822-ef3d433a3cfa?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1374&q=80',
    map: '',
    gallery: [
      'https://images.unsplash.com/photo-1576220258822-153014832245?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1772&q=80',
      'https://images.unsplash.com/photo-1584837140804-599306fb37f9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
      'https://images.unsplash.com/photo-1606233412480-92c787ea6d36?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxzZWFyY2h8MjF8fGdvbGYlMjBiYWxsfGVufDB8fDB8fA%3D%3D&auto=format&fit=crop&w=800&q=60',
      'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1476&q=80',
      'https://images.unsplash.com/photo-1567019001814-57350903e2af?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1474&q=80'
    ],
    share: {
      url: 'www.example.com',
      title: 'title',
      text: 'text'
    }
  },
  course: {
    name: 'COURSE_NAME'
  }
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const routerStore = useRouterStore()
const toastsStore = useToastsStore()
const pageTitle = ref(playerTitle())

useHead({
  title: pageTitle
})

// Inputs

// Outputs
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const idx = ref(1)
const course = ref()
const hole = ref()
const count = ref(1)
const bannerSrc = ref('')
const bannerSrcset = ref('')
const shareUrl = ref('')
const holeCount = ref(0)
const share = computed(() => ({
  label: `SHARE HOLE ${props.holeIndex}`,
  shareText: 'Share hole text',
  shareTitle: 'Share hole title'
}))

// Helper Functions
onMounted(() => {
  shareUrl.value = window.location.href
})

const reset = () => {
  loading.value = false
  serverError.value = false
}

const handleToastSuccess = () => {
  toastsStore.pushToast({
    content: 'The URL has been copied to clipboard!',
    icon: 'check-circle',
    semantic: ToastSemantic.Success
  })
}

let courseSlug = ''

// Query Functions
const fetchData = async () => {
  reset()

  // Prepare Inputs + any Formating

  // Validate Inputs (optional, mainly for validating form inputs)

  // Fetch Data
  try {
    loading.value = true
    const slug = getSlug(props.slug || '')
    const holeIndex = parseInt(props.holeIndex || '')
    if (isNaN(holeIndex) || holeIndex < 1) {
      routerStore.is404 = true
      return
    }
    idx.value = holeIndex

    const { data: courseValue } = await client.query<getCourse>({
      query: GET_COURSE_QUERY,
      variables: {
        slug
      },
      fetchPolicy: 'cache-first'
    })

    const { data: holeValue } = await client.query<getHolesForCourse>({
      query: GET_HOLES_FOR_COURSE_QUERY,
      variables: {
        slug,
        offset: idx.value - 1,
        limit: 1
      },
      fetchPolicy: 'cache-first'
    })

    if (!props.slug) {
      routerStore.is404 = true
      return
    } else if (
      !courseValue ||
      courseValue.getCourse.__typename !== 'GetCourseOutput' ||
      !courseValue.getCourse.course ||
      holeValue.listHoles.__typename !== 'ListHolesOutput' ||
      idx.value < 1 ||
      (holeValue.listHoles.count && idx.value > holeValue.listHoles.count)
    ) {
      routerStore.is404 = true
      return
    }
    holeCount.value = holeValue.listHoles.count

    const expectedSlug = createSlug(
      courseValue.getCourse.course.courseName,
      courseValue.getCourse.course.slug
    )

    courseSlug = expectedSlug

    if (props.slug !== expectedSlug) {
      routerStore.redirect.code = 302
      await router.replace({
        params: {
          ...route.params,
          slug: expectedSlug
        }
      })
    }

    course.value = courseValue.getCourse.course
    hole.value = holeValue.listHoles.holes[0]
    count.value = holeValue.listHoles.count
    const [src, srcset] = ugcImageSrc(hole.value.gallery[0])
    bannerSrc.value = src
    bannerSrcset.value = srcset
    pageTitle.value = playerTitle(
      `${courseValue.getCourse.course.courseName} hole #${props.holeIndex}`
    )
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

// Fetch Data when the URL changes the props
watch(props, async () => {
  await fetchData()
})

// Fetch data when the view is first rendered
await fetchData()
</script>

<template>
  <div class="hole-single">
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <HeaderContent type="secondary">
          <template #secondary-left>
            <LinkWrapper
              :link="{
                to: {
                  name: coursesRouteNames.CourseSingle,
                  params: {
                    slug: props.slug
                  }
                }
              }"
            >
              <USButton
                type="inline"
                icon="arrow-left"
                :label="`Back to ${course.courseName}`"
              />
            </LinkWrapper>
          </template>
          <template #secondary-right>
            <USShareButton
              label="Share hole"
              :share-url="shareUrl"
              :share-title="share.title"
              :share-text="share.text"
              @succuss="handleToastSuccess"
            />
          </template>
        </HeaderContent>
      </div>
    </div>
    <div class="grid__row">
      <div
        :class="[
          'grid__column grid__column--12',
          { 'grid__column--breakpoint--7--6': hole.map }
        ]"
      >
        <h2 class="heading--h2 margin-bottom--6">
          {{ `Hole #${props.holeIndex}` }}
        </h2>

        <p class="body--p1--regular margin-bottom--5">
          {{ hole.description }}
        </p>

        <p
          class="body--p1--semibold text-transform--uppercase margin-bottom--8"
        >
          Par
        </p>
        <p class="heading--h3">{{ hole.par }}</p>
      </div>
      <div
        v-if="hole.map"
        class="grid__column grid__column--12 grid__column--breakpoint--7--6"
      >
        <!-- TODO,
          - confirm if image has fixed height. If so, what are values in various viewports
        -->
        <ImageWrapper
          class="hole-single__map-image"
          :src="ugcImageSrc(hole.map)[0]"
          height="430px"
          object-fit="contain"
        />
      </div>
    </div>

    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <div class="hole-single__hole-nav margin-top--5">
          <template v-if="parseInt(props.holeIndex, 10) > 1">
            <LinkWrapper
              :link="{
                to: {
                  name: coursesRouteNames.HoleSingle,
                  params: {
                    holeIndex: idx - 1
                  }
                }
              }"
            >
              <USButton
                type="inline"
                :label="`PREV: HOLE ${parseInt(props.holeIndex, 10) - 1}`"
                class="margin-right--5"
              />
            </LinkWrapper>
          </template>

          <template
            v-if="
              parseInt(props.holeIndex, 10) < 18 && props.holeIndex < holeCount
            "
          >
            <LinkWrapper
              :link="{
                to: {
                  name: coursesRouteNames.HoleSingle,
                  params: {
                    holeIndex: idx + 1
                  }
                }
              }"
            >
              <USButton
                type="inline"
                :label="`NEXT: HOLE ${parseInt(props.holeIndex, 10) + 1}`"
              />
            </LinkWrapper>
          </template>
        </div>
      </div>
    </div>

    <USMediaSlider
      v-if="hole.gallery && hole.gallery.length"
      class="hole-single__media-slider"
    >
      <USMediaSlide
        v-for="(imageUrl, index) in hole.gallery"
        :key="index"
        :img-args="{ src: ugcImageSrc(imageUrl)[0] }"
      />
    </USMediaSlider>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

.hole-single {
  &__hole-nav {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      justify-content: start;
    }
  }

  &__map-image {
    margin-bottom: var(--spacing--6);
    margin-top: var(--spacing--6);

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      margin-bottom: 0;
      margin-top: 0;
    }
  }

  &__media-slider {
    margin-top: var(--spacing--6);

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      margin-top: var(--spacing--4);
    }
  }
}
</style>
